import {Grid, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {labelTextStyle, NumberFormatCustom, SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import React, {useEffect, useState} from 'react';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {observer} from 'mobx-react-lite';
import EmploymentQuestions from '../../Questions/Employment';
import ExistingPolicies from '../../Questions/ExistingPolicies';
import {SelectInputElement} from './SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/PrequalXref';
import {shouldShowHHIWarning, WarningMessage} from '@/Agent/Common/WarningMessage';
import RootStore from '../../../Core/mobx/RootStore';
import {AB_FLAGS} from '../../../Core/constants/abFlags';

const useStyles = makeStyles({
    icon: {
        marginRight: 4,
    },
    approved: {
        color: 'green',
    },
    denied: {
        color: 'red',
    },
    sectionTitle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 46,
        width: 46,
        borderRadius: '50%',
        backgroundColor: '#E9ECFF',
        color: '#3F51B5',
        marginRight: 21,
        fontSize: 26,
        textAlign: 'center',
    },
    sectionTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
    },
    extraQuestionsBox: {
        margin: '56px 8px 0',
        boxShadow: '0px 0px 5px 0px #00000033',
    },
    extraQuestionsLabel: {
        marginBottom: 16,
        fontFamily: 'Fredoka',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '100%',
    },
    separatedLabel: {
        display: 'inline-flex',
        marginBottom: 42,
    },
    mt: {
        marginTop: 56,
    },
});

type FinancesProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Prequal;
    walkthroughData: any | null;
};

const Finances = observer(({valueChanged, parameters, walkthroughData}: FinancesProps) => {
    const classes = useStyles();

    const rawHHI = parameters.incomeHouseholdText?.value ?? '';
    const hhi = Number(String(rawHHI).replace(/[,$]/g, '')) || 0;

    const monthlyBudget = Number(parameters.monthlyBudget?.value) || 0;

    // Get the AB flag value from the parameter store
    const lowerHouseholdIncomeLimit =
        RootStore.agentPortalStore.parameterStore.abFlags[AB_FLAGS.LOWER_HOUSEHOLD_INCOME_LIMIT] === true;

    const {exceeds: showPremiumWarning, threshold: premiumThreshold} = shouldShowHHIWarning({
        premium: monthlyBudget,
        annualHHI: hhi,
        premiumMode: 'Monthly',
        lowerHouseholdIncomeLimit,
    });

    const [coverageAmount, setCoverageAmount] = useState<number>(0);

    useEffect(() => {
        setCoverageAmount(walkthroughData?.recommended?.coverageAmount);
    }, [walkthroughData?.recommended?.coverageAmount]);

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    return (
        <Grid key={'Behavior'} container spacing={2} style={{marginBottom: 56}}>
            <Grid item>
                <div className={classes.sectionTitleWrapper}>
                    <span className={classes.sectionTitle}>2</span>
                    <Typography
                        style={{
                            fontSize: 24,
                            fontWeight: 700,
                            fontFamily: 'LoraSemiBold',
                            lineHeight: '120%',
                            letterSpacing: 0,
                        }}
                    >
                        Finances
                    </Typography>
                </div>
            </Grid>
            <div style={{width: '100%', marginBottom: 32}} />

            <EmploymentQuestions valueChanged={valueChanged} parameters={parameters} prequal />

            <Grid item xs={4}>
                <Typography className={classes.separatedLabel} style={labelTextStyle}>
                    <span>{required('')}</span>
                    <span>Annual Household Income</span>
                </Typography>
                <TextBox
                    id={'incomeHouseholdText'}
                    value={parameters.incomeHouseholdText?.value}
                    onChange={(value) =>
                        valueChanged({...parameters.incomeHouseholdText, name: 'incomeHouseholdText'}, value)
                    }
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                        style: {
                            height: 48,
                        },
                    }}
                    style={{
                        width: '100%',
                    }}
                />
            </Grid>
            {/* <Grid item xs={4}>
                <TextBox
                    id={'incomeIndividual'}
                    label={'Personal Income'}
                    value={parameters.incomeIndividual?.value}
                    onChange={(value) =>
                        valueChanged({...parameters.incomeIndividual, name: 'incomeIndividual'}, value)
                    }
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid> */}

            <Grid item xs={4}>
                <Typography className={classes.separatedLabel} style={labelTextStyle}>
                    <span>{required('')}</span>
                    <span>Monthly Budget</span>
                </Typography>
                <TextBox
                    id={'monthlyBudget'}
                    value={parameters.monthlyBudget?.value || 200}
                    onChange={(value) => valueChanged(parameters.monthlyBudget, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                        style: {
                            height: 48,
                        },
                    }}
                    style={{
                        width: '100%',
                    }}
                />
                {showPremiumWarning && <WarningMessage value={premiumThreshold} newDesign />}
            </Grid>

            <Grid item xs={4} className={classes.mt}>
                <Typography className={classes.separatedLabel} style={labelTextStyle}>
                    <span>{required('')}</span>
                    <span>Coverage Amount</span>
                </Typography>
                <TextBox
                    id={'recommendedCoverageAmount'}
                    value={coverageAmount}
                    onChange={(value) => valueChanged(parameters.recommendedCoverageAmount, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                        style: {
                            height: 48,
                        },
                    }}
                    style={{
                        width: '100%',
                    }}
                />
            </Grid>
            <Grid item xs={4} className={classes.mt}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Are you currently in the military?',
                        ...parameters.isCurrentlyMilitary,
                        name: 'isCurrentlyMilitary',
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4} className={classes.mt}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Existing Coverage',
                        ...parameters.existingCoverage,
                        name: 'existingCoverage',
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.isCurrentlyMilitary.value && (
                <Grid
                    item
                    xs={12}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 34px',
                    }}
                >
                    <Grid item xs={10} sm={8} md={6} lg={5}>
                        <Typography className={classes.extraQuestionsLabel}>
                            Are you currently in the military?
                        </Typography>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Current or future deployment orders',
                                ...parameters.currentDeploymentOrders,
                                name: 'currentDeploymentOrders',
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                </Grid>
            )}
            {parameters.existingCoverage.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Existing Coverage</Typography>
                    </Grid>
                    <Grid container item xs={12} spacing={4} style={{marginBottom: 32}}>
                        <Grid item xs={6}>
                            <SelectInputElement
                                param={{
                                    label: 'Policy Type',
                                    ...parameters.existingPolicyType,
                                    name: 'existingPolicyType',
                                    options: fieldOptions.existingPolicyType.options,
                                }}
                                required={required('')}
                                valueChanged={valueChanged}
                                newLabelType
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography className={classes.separatedLabel} style={labelTextStyle}>
                                <span>{required('')}</span>
                                <span>Coverage amount of your existing policy</span>
                            </Typography>
                            <TextBox
                                id={'existingCoverageAmount'}
                                placeholder="Coverage Amount Of Your Existing Policy"
                                value={parameters.existingCoverageAmount?.value}
                                onChange={(value) =>
                                    valueChanged(
                                        {...parameters.existingCoverageAmount, name: 'existingCoverageAmount'},
                                        value,
                                    )
                                }
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                    style: {
                                        height: 48,
                                    },
                                }}
                                style={{
                                    width: '100%',
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={6}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Replacing Existing Policy?',
                                ...parameters.replacingExistingPolicy,
                                name: 'replacingExistingPolicy',
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
});

export default Finances;
